import React, { useState } from 'react';
import clsx from 'clsx';
import { format, fromUnixTime } from 'date-fns';
import { ROYALTY_BPS, shortenHash } from '@soundxyz/utils/const';
import type { Maybe } from '@soundxyz/utils/helpers';
import { styled } from '@components-shared/src/stitches';
import { Button } from '@components/main-components/button/Button';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { Tooltip } from '@components/main-components/tooltip/Tooltip';
import { DASH } from '@utils/constants';
import {
  etherscanForContract,
  formatWeiToEthForDisplay,
  humanReadableChainName,
} from '@utils/helpers';
import { HeaderContainer, SectionTitle } from '../shared';

// Missing Test
// TODO: https://linear.app/sound/issue/ENG-2512/add-story-for-contractdetailssection
export const ContractDetailsSection = ({
  contractAddress,
  splitsContractAddress,
  mintStartTime,
  chainId,
  mints,
  totalVolume,
  uniqueCollectors,
}: {
  contractAddress: Maybe<string>;
  mintStartTime: Maybe<number>;
  splitsContractAddress?: Maybe<string>;
  chainId: number;
  mints: number;
  totalVolume: string;
  uniqueCollectors: number;
}) => {
  const [show, setShow] = useState(false);
  const viewOnEtherscan = (contractAddr: string) => (
    <Tooltip openOnClick={false}>
      <Tooltip.Trigger className="cursor-pointer">
        <Info>{shortenHash(contractAddr, 6, 6)}</Info>
      </Tooltip.Trigger>
      <Tooltip.StyledContent>View on Etherscan</Tooltip.StyledContent>
    </Tooltip>
  );

  const sections: {
    title: string;
    content: React.ReactNode;
    hideIfEmpty?: boolean;
  }[] = [
    {
      title: 'Release Date',
      content: !!mintStartTime
        ? format(fromUnixTime(mintStartTime).getTime(), 'MMMM dd, yyyy')
        : null,
      hideIfEmpty: true,
    },
    {
      title: 'Mints',
      content: mints > 0 ? mints : null,
    },
    {
      title: 'Total Volume',
      content:
        totalVolume && parseInt(totalVolume) > 0
          ? `${formatWeiToEthForDisplay({ weiValue: totalVolume, maxDecimals: 4 })} ETH`
          : null,
    },
    {
      title: 'Unique Collectors',
      content: uniqueCollectors > 0 ? uniqueCollectors : null,
    },
    {
      title: 'Song Contract Address',
      content: contractAddress ? (
        <AccessibleLink
          absoluteUri={etherscanForContract({ contractAddress, chainId })}
          type="external"
        >
          {viewOnEtherscan(contractAddress)}
        </AccessibleLink>
      ) : null,
    },
    {
      title: 'Splits Contract Address',
      content: splitsContractAddress ? (
        <AccessibleLink
          absoluteUri={etherscanForContract({ contractAddress: splitsContractAddress, chainId })}
          type="external"
        >
          {viewOnEtherscan(splitsContractAddress)}
        </AccessibleLink>
      ) : null,
      hideIfEmpty: true,
    },
    {
      title: 'Blockchain',
      content: humanReadableChainName(chainId),
    },
    {
      title: 'Token Standard',
      content: 'ERC-721',
    },
    {
      title: 'Resale Royalty',
      content: `${ROYALTY_BPS / 100}%`,
    },
  ];

  return (
    <div className={clsx('grid gap-5', !show && 'mt-6')}>
      {show && (
        <div className="mt-8 flex flex-col md:mt-10">
          <HeaderContainer css={{ padding: 0 }}>
            <SectionTitle title="Contract Details" />
          </HeaderContainer>
          <div className="mt-4">
            {sections.map(({ title, content, hideIfEmpty = false }) => {
              if (hideIfEmpty && !content) return null;
              return (
                <Section key={title}>
                  <Title>{title}</Title>
                  <Info>{content ?? <span className="opacity-30">{DASH}</span>}</Info>
                </Section>
              );
            })}
          </div>
        </div>
      )}
      <Button
        variant="tertiary"
        onClick={() => setShow(!show)}
        label={`${show ? 'Hide' : 'Show'} contract details`}
        className="w-full max-w-none md:w-auto md:max-w-fit"
      />
    </div>
  );
};

const Section = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1px solid $gray300',
  paddingY: 12,
});

const Title = styled('h5', {
  base: 'S',
  textTransform: 'uppercase',
  color: '$neutral500',
});

const Info = styled('h4', {
  base: 'M',
  fontWeight: 600,
  '.etherscan-tooltip': {
    display: 'none',
  },
  '&:hover': {
    '.etherscan-tooltip': {
      dislay: 'block',
    },
  },
});
